import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { getDealRoomBrandedName } from '../../../../utils/locale';
import Card from 'ui/canon/Card';
import Input from 'ui/canon/Input';
import FormField from 'ui/canon/FormField';

const DealInformationForm = ({ formData, updateFormData, errors, company, currentUser }) => {
  const intl = useIntl();
  const dealRoomBrandedName = getDealRoomBrandedName(company) || intl.formatMessage({ id: 'succession.messaging.chat_panel.deal_room_label' });
  const agentWord = company?.agent_word || 'Advisor';
  
  const handleChange = (field) => (e) => {
    updateFormData({ [field]: e.target.value });
  };

  // Pricing logic
  const isPricingTBD = !formData.list_price && formData.list_price !== 0;
  const isPricingCustom = formData.list_price !== null && formData.list_price !== undefined;

  const handlePricingChange = (type) => {
    if (type === 'tbd') {
      updateFormData({ list_price: null });
    } else {
      updateFormData({ list_price: '' });
    }
  };

  const formatCurrency = (value) => {
    if (!value || value === '') return '';
    const numValue = parseFloat(value.toString().replace(/[^\d.-]/g, ''));
    if (isNaN(numValue)) return '';
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue);
  };

  const handlePriceInputChange = (e) => {
    const value = e.target.value.replace(/[^\d.-]/g, '');
    updateFormData({ list_price: value });
  };

  // Check if list price should be hidden based on deal type and company settings
  const hideListPrice = 
    (formData.deal_type === 'continuity' && company?.hide_list_price_in_deal_room_wizard_continuity) ||
    (formData.deal_type === 'succession' && company?.hide_list_price_in_deal_room_wizard_succession) ||
    (formData.deal_type === 'sale' && company?.hide_list_price_in_deal_room_wizard_sale);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-succession-text-primary">
          <FormattedMessage id="succession_deals.wizard.steps.deal_information.title" />
        </h2>
        <p className="mt-2 text-lg text-succession-text-secondary">
          <FormattedMessage 
            id="succession_deals.wizard.steps.deal_information.description" 
            values={{ dealType: formData.deal_type }}
          />
        </p>
      </div>

      <div className="space-y-8">
        {!hideListPrice && (
          <>
            {/* Premium Pricing Selection */}
            <Card variant="subtle" size="lg" className="rounded-xl">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-succession-text-primary mb-2">
                  <FormattedMessage id="succession_deals.wizard.steps.deal_information.pricing.title" />
                </h3>
                <p className="text-sm text-succession-text-secondary">
                  <FormattedMessage id="succession_deals.wizard.steps.deal_information.pricing.description" />
                </p>
              </div>
          
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {/* TBD Option */}
            <div 
              onClick={() => handlePricingChange('tbd')}
              className={`relative cursor-pointer rounded-lg border-2 p-6 transition-all duration-300 hover:shadow-lg ${
                isPricingTBD 
                  ? 'border-indigo-500 bg-indigo-50 shadow-md ring-2 ring-indigo-200' 
                  : 'border-succession-border bg-succession-bg-primary hover:border-succession-primary hover:bg-succession-bg-secondary'
              }`}
            >
              <div className="flex items-start space-x-4">
                <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 mt-1 transition-all duration-200 ${
                  isPricingTBD 
                    ? 'border-indigo-500 bg-indigo-500' 
                    : 'border-succession-border bg-succession-bg-primary'
                }`}>
                  {isPricingTBD && (
                    <div className="w-full h-full rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-2xl">💭</span>
                    <h4 className="text-lg font-semibold text-succession-text-primary">
                      <FormattedMessage id="succession_deals.wizard.steps.deal_information.pricing.tbd.title" />
                    </h4>
                  </div>
                  <p className="text-sm text-succession-text-secondary leading-relaxed">
                    <FormattedMessage id="succession_deals.wizard.steps.deal_information.pricing.tbd.description" />
                  </p>
                  <div className="mt-3 flex items-center space-x-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                      <FormattedMessage id="succession_deals.wizard.steps.deal_information.pricing.tbd.badge_flexible" />
                    </span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <FormattedMessage id="succession_deals.wizard.steps.deal_information.pricing.tbd.badge_negotiable" />
                    </span>
                  </div>
                </div>
              </div>
              {isPricingTBD && (
                <div className="absolute top-3 right-3">
                  <div className="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              )}
            </div>

            {/* Custom Price Option */}
            <div 
              onClick={() => handlePricingChange('custom')}
              className={`relative cursor-pointer rounded-lg border-2 p-6 transition-all duration-300 hover:shadow-lg ${
                isPricingCustom 
                  ? 'border-indigo-500 bg-indigo-50 shadow-md ring-2 ring-indigo-200' 
                  : 'border-succession-border bg-succession-bg-primary hover:border-succession-primary hover:bg-succession-bg-secondary'
              }`}
            >
              <div className="flex items-start space-x-4">
                <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 mt-1 transition-all duration-200 ${
                  isPricingCustom 
                    ? 'border-indigo-500 bg-indigo-500' 
                    : 'border-succession-border bg-succession-bg-primary'
                }`}>
                  {isPricingCustom && (
                    <div className="w-full h-full rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-2xl">💰</span>
                    <h4 className="text-lg font-semibold text-succession-text-primary">
                      <FormattedMessage id="succession_deals.wizard.steps.deal_information.pricing.custom.title" />
                    </h4>
                  </div>
                  <p className="text-sm text-succession-text-secondary leading-relaxed">
                    <FormattedMessage id="succession_deals.wizard.steps.deal_information.pricing.custom.description" />
                  </p>
                  <div className="mt-3 flex items-center space-x-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <FormattedMessage id="succession_deals.wizard.steps.deal_information.pricing.custom.badge_transparent" />
                    </span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      <FormattedMessage id="succession_deals.wizard.steps.deal_information.pricing.custom.badge_precise" />
                    </span>
                  </div>
                </div>
              </div>
              {isPricingCustom && (
                <div className="absolute top-3 right-3">
                  <div className="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Custom Price Input */}
          {isPricingCustom && (
            <div className="transform transition-all duration-500 ease-out opacity-100 translate-y-0">
              <Card variant="outlined" size="md">
                <label htmlFor="custom_price" className="block text-sm font-medium text-succession-text-secondary mb-3">
                  <FormattedMessage id="succession_deals.wizard.steps.deal_information.pricing.custom.asking_price_label" />
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  </div>
                  <Input
                    id="custom_price"
                    type="text"
                    value={formData.list_price || ''}
                    onChange={handlePriceInputChange}
                    className="pl-8 pr-4 py-3 text-lg font-medium"
                    placeholder={intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_information.pricing.custom.price_placeholder' })}
                  />
                  {formData.list_price && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <span className="text-sm text-succession-text-muted font-medium">
                        {formatCurrency(formData.list_price)}
                      </span>
                    </div>
                  )}
                </div>
                <p className="mt-2 text-xs text-succession-text-muted flex items-center space-x-1">
                  <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <span>
                    <FormattedMessage id="succession_deals.wizard.steps.deal_information.pricing.custom.numbers_only_help" />
                  </span>
                </p>
              </Card>
            </div>
          )}

          {/* Important Notice */}
          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-amber-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h4 className="text-sm font-medium text-amber-800">
                  <FormattedMessage id="succession_deals.wizard.steps.deal_information.pricing.update_notice.title" />
                </h4>
                <p className="mt-1 text-sm text-amber-700 leading-relaxed">
                  <FormattedMessage id="succession_deals.wizard.steps.deal_information.pricing.update_notice.description" />
                </p>
              </div>
            </div>
          </div>
        </Card>
          </>
        )}
        
        {hideListPrice && (
          <Card variant="subtle" size="lg" className="text-center">
            <p className="text-succession-text-secondary">
              <FormattedMessage 
                id="succession_deals.wizard.steps.deal_information.pricing.list_price_not_required" 
                values={{ dealType: formData.deal_type }}
              />
            </p>
            <p className="text-sm text-succession-text-muted mt-2">
              <FormattedMessage id="succession_deals.wizard.steps.deal_information.pricing.proceed_without_price" />
            </p>
          </Card>
        )}

        {/* Headline */}
        <FormField
          id="headline"
          label={<FormattedMessage id="succession_deals.wizard.steps.deal_information.fields.headline.label" />}
          required
          type="text"
          value={formData.headline}
          onChange={handleChange('headline')}
          maxLength={100}
          placeholder={intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_information.fields.headline.placeholder' }, { agent_word: agentWord })}
          error={errors.headline}
          helpText={<FormattedMessage id="succession_deals.wizard.steps.deal_information.fields.headline.help_text" />}
        />

        {/* Sub-headline */}
        {!company?.teaser_hide_business_overview_question && (
          <FormField
            id="sub_headline"
            label={<FormattedMessage id="succession_deals.wizard.steps.deal_information.fields.business_overview.label" />}
            required
            type="textarea"
            rows={4}
            value={formData.sub_headline}
            onChange={handleChange('sub_headline')}
            maxLength={500}
            placeholder={intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_information.fields.business_overview.placeholder' })}
            error={errors.sub_headline}
            helpText={<FormattedMessage id="succession_deals.wizard.steps.deal_information.fields.business_overview.help_text" />}
          />
        )}

        {/* Summary of Business */}
        <FormField
          id="summary_of_business"
          label={<FormattedMessage id="succession_deals.wizard.steps.deal_information.fields.summary_of_business.label" />}
          required
          type="textarea"
          rows={4}
          value={formData.summary_of_business}
          onChange={handleChange('summary_of_business')}
          maxLength={500}
          placeholder={intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_information.fields.summary_of_business.placeholder' }, { dealRoomBrandedName })}
          error={errors.summary_of_business}
          helpText={<FormattedMessage id="succession_deals.wizard.steps.deal_information.fields.summary_of_business.help_text" />}
        />

        {/* Ideal Candidate */}
        <FormField
          id="ideal_candidate"
          label={<FormattedMessage id="succession_deals.wizard.steps.deal_information.fields.ideal_candidate.label" />}
          required
          type="textarea"
          rows={3}
          value={formData.ideal_candidate}
          onChange={handleChange('ideal_candidate')}
          maxLength={300}
          placeholder={intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_information.fields.ideal_candidate.placeholder' })}
          error={errors.ideal_candidate}
          helpText={<FormattedMessage id="succession_deals.wizard.steps.deal_information.fields.ideal_candidate.help_text" />}
        />

        {/* Growth Opportunities */}
        <FormField
          id="growth_opportunities"
          label={<FormattedMessage id="succession_deals.wizard.steps.deal_information.fields.growth_opportunities.label" />}
          required
          type="textarea"
          rows={4}
          value={formData.growth_opportunities}
          onChange={handleChange('growth_opportunities')}
          maxLength={500}
          placeholder={intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_information.fields.growth_opportunities.placeholder' })}
          error={errors.growth_opportunities}
          helpText={<FormattedMessage id="succession_deals.wizard.steps.deal_information.fields.growth_opportunities.help_text" />}
        />

        {/* Assets Included */}
        <FormField
          id="assets_included"
          label={<FormattedMessage id="succession_deals.wizard.steps.deal_information.fields.assets_included.label" />}
          required
          type="textarea"
          rows={4}
          value={formData.assets_included}
          onChange={handleChange('assets_included')}
          maxLength={500}
          placeholder={intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_information.fields.assets_included.placeholder' })}
          error={errors.assets_included}
          helpText={<FormattedMessage id="succession_deals.wizard.steps.deal_information.fields.assets_included.help_text" />}
        />

        {/* Intangible Assets Included */}
        <FormField
          id="intangible_assets_included"
          label={<FormattedMessage id="succession_deals.wizard.steps.deal_information.fields.intangible_assets_included.label" />}
          required
          type="textarea"
          rows={3}
          value={formData.intangible_assets_included}
          onChange={handleChange('intangible_assets_included')}
          maxLength={500}
          placeholder={intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_information.fields.intangible_assets_included.placeholder' })}
          error={errors.intangible_assets_included}
          helpText={<FormattedMessage id="succession_deals.wizard.steps.deal_information.fields.intangible_assets_included.help_text" />}
        />

        {/* Client Demographics */}
        <FormField
          id="customer_demographics_comments"
          label={<FormattedMessage id="succession_deals.wizard.steps.deal_information.fields.client_demographics.label" />}
          required
          type="textarea"
          rows={4}
          value={formData.customer_demographics_comments}
          onChange={handleChange('customer_demographics_comments')}
          maxLength={300}
          placeholder={intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_information.fields.client_demographics.placeholder' })}
          error={errors.customer_demographics_comments}
          helpText={<FormattedMessage id="succession_deals.wizard.steps.deal_information.fields.client_demographics.help_text" />}
        />

        {/* Specializations */}
        <FormField
          id="products_and_services_comments"
          label={<FormattedMessage id="succession_deals.wizard.steps.deal_information.fields.products_and_services.label" />}
          required
          type="textarea"
          rows={4}
          value={formData.products_and_services_comments}
          onChange={handleChange('products_and_services_comments')}
          maxLength={500}
          placeholder={intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_information.fields.products_and_services.placeholder' })}
          error={errors.products_and_services_comments}
          helpText={<FormattedMessage id="succession_deals.wizard.steps.deal_information.fields.products_and_services.help_text" />}
        />
      </div>

      {/* Tips Section */}
      <Card variant="subtle" size="md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-succession-text-primary">
              <FormattedMessage id="succession_deals.wizard.steps.deal_information.tips.title" />
            </h3>
            <div className="mt-2 text-sm text-succession-text-secondary">
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <FormattedMessage id="succession_deals.wizard.steps.deal_information.tips.tip1" />
                </li>
                <li>
                  <FormattedMessage id="succession_deals.wizard.steps.deal_information.tips.tip2" />
                </li>
                <li>
                  <FormattedMessage id="succession_deals.wizard.steps.deal_information.tips.tip3" />
                </li>
                <li>
                  <FormattedMessage id="succession_deals.wizard.steps.deal_information.tips.tip4" />
                </li>
                <li>
                  <FormattedMessage id="succession_deals.wizard.steps.deal_information.tips.tip5" />
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

DealInformationForm.propTypes = {
  formData: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
  errors: PropTypes.object,
  company: PropTypes.object,
  currentUser: PropTypes.object
};

DealInformationForm.defaultProps = {
  errors: {},
  company: {},
  currentUser: null
};

export default DealInformationForm; 